/**
 * SOLOMON — CONVERSATION HISTORY SIDEBAR
 * Searchable list of past conversations.
 * Click to load, shows date + preview + models used.
 * Collapsible on mobile via hamburger toggle.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
// Model badges here are PROVIDER-level on purpose: the list dedupes labels, so a
// thread that used Opus and Sonnet shows one "Claude" chip rather than two. The
// precise brain is shown per-message instead (MessageBubble, via getModelConfig
// in ChatInterface/modelBadgeConfig.js). Resolution is family-based — see
// src/lib/modelRegistry.js.
import { getProviderLabel as getModelLabel } from '../../lib/modelRegistry.js';
import './sidebar.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function ConversationSidebar({
  isOpen,
  onToggle,
  onSelectConversation,
  activeConversationId,
  onNewChat,
  // Track A: nav rail
  activeTool,
  onSetActiveTool,
  // Capability surface entry point
  onOpenCapabilities,
  // Settings entry point. The chrome's gear went with the icon row, and the
  // Capabilities map only surfaces Settings on a conditional attention row —
  // so without this the panel (and the theme toggle inside it) has no
  // unconditional route.
  onOpenSettings,
}) {
  const { authFetch } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [search, setSearch]               = useState('');
  const [loading, setLoading]             = useState(false);
  const searchRef = useRef(null);

  // Fetch conversation list
  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/chat/conversations?limit=50`);
      if (!res.ok) throw new Error('Failed to fetch conversations');
      const data = await res.json();
      setConversations(data.conversations || data.data || []);
    } catch (err) {
      console.warn('[Solomon/Sidebar] Failed to load conversations:', err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  // Load conversations when sidebar opens
  useEffect(() => {
    if (isOpen) {
      fetchConversations();
      // Focus search on open (desktop)
      setTimeout(() => searchRef.current?.focus(), 200);
    }
  }, [isOpen, fetchConversations]);

  // Filter by search term
  const filtered = conversations.filter(conv => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const preview = (conv.firstMessage || conv.first_message || '').toLowerCase();
    return preview.includes(q);
  });

  // Format relative date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d     = new Date(dateStr);
    const now   = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7)   return `${diffDays} days ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Truncate preview text
  const truncate = (text, max = 60) => {
    if (!text) return 'Empty conversation';
    return text.length > max ? text.slice(0, max) + '...' : text;
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && <div className="sidebar-backdrop" onClick={onToggle} />}

      {/* `inert` when closed. The sidebar is never unmounted — closed it is only
          translateX(-100%), so without this every control inside (Capabilities,
          New Conversation, search, the whole history list, Settings) stays in the
          tab order of a panel the user cannot see. Learn's mounted-hidden host is
          the correct counter-example: `display:none` really does remove it. */}
      <aside
        className={`conversation-sidebar ${isOpen ? 'conversation-sidebar--open' : ''}`}
        inert={!isOpen ? '' : undefined}
        aria-hidden={!isOpen || undefined}
      >
        {/* Sidebar header */}
        <div className="sidebar-header">
          <h2 className="sidebar-title">Conversations</h2>
          <button className="sidebar-close-btn" onClick={onToggle} title="Close sidebar">
            <CloseIcon />
          </button>
        </div>

        {/* Leads quick-nav removed — Leads now lives in the Wholesale workspace. */}

        {/* Capability map entry — deliberately identical in treatment to the
            New Conversation button below it, and placed ABOVE it so the
            repertoire is the first thing the sidebar offers. Closes the
            sidebar after opening, the convention every selection here follows. */}
        <button
          type="button"
          className="sidebar-new-chat"
          onClick={() => { onOpenCapabilities?.(); onToggle(); }}
        >
          <GridIcon /> Capabilities
        </button>

        {/* New chat button */}
        <button className="sidebar-new-chat" onClick={() => { onNewChat(); onToggle(); }}>
          <PlusIcon /> New Conversation
        </button>

        {/* Search */}
        <div className="sidebar-search">
          <SearchIcon />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search conversations..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="sidebar-search-input"
          />
          {search && (
            <button className="sidebar-search-clear" onClick={() => setSearch('')}>
              <CloseIcon size={12} />
            </button>
          )}
        </div>

        {/* Conversation list */}
        <div className="sidebar-list">
          {loading && (
            <div className="sidebar-loading">
              <span className="sidebar-loading-dot" />
              <span className="sidebar-loading-dot" />
              <span className="sidebar-loading-dot" />
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="sidebar-empty">
              {search ? 'No matches found' : 'No conversations yet'}
            </div>
          )}

          {filtered.map(conv => {
            const id      = conv.id;
            const preview = conv.firstMessage || conv.first_message || '';
            const date    = conv.updatedAt || conv.updated_at || conv.createdAt || conv.created_at;
            const isActive = id === activeConversationId;

            // Collect unique model labels from the conversation
            const models = [];
            if (conv.models) {
              conv.models.forEach(m => {
                const label = getModelLabel(m);
                if (label && !models.includes(label)) models.push(label);
              });
            }

            return (
              <button
                key={id}
                className={`sidebar-item ${isActive ? 'sidebar-item--active' : ''}`}
                onClick={() => { onSelectConversation(id); onToggle(); }}
              >
                <div className="sidebar-item-top">
                  <span className="sidebar-item-date">{formatDate(date)}</span>
                </div>
                <div className="sidebar-item-preview">{truncate(preview)}</div>
                {models.length > 0 && (
                  <div className="sidebar-item-models">
                    {models.map(m => (
                      <span key={m} className="sidebar-model-tag">{m}</span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer — Settings. Pinned below the scrolling list so it stays
            reachable however long the conversation history gets. */}
        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-footer-btn"
            onClick={() => { onOpenSettings?.(); onToggle(); }}
            aria-label="Open settings"
          >
            <GearIcon /> Settings
          </button>
        </div>

      </aside>
    </>
  );
}

// ── Icons ──

function CloseIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function GridIcon() {
  // 3x3 grid — the repertoire/"everything Solomon can do" affordance. Sized to
  // match PlusIcon so the two sidebar-new-chat buttons are visually identical.
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}


// Hamburger icon exported for use in ChatHeader
export function HamburgerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}
